import { useState, useEffect, useRef, useContext } from "react";
import { ChevronDown, Briefcase, Check } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import useApiMethods from "../../lib/useApiMethods";
import { PROJECTS_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

export default function ProjectSwitcher() {
    const { currentProject, switchProject } = useContext(AuthContext);
    const { fetch } = useApiMethods();
    const [projects, setProjects] = useState([]);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    // Keep a ref so loadProjects always sees the latest currentProject value
    const currentProjectRef = useRef(currentProject);
    useEffect(() => { currentProjectRef.current = currentProject; }, [currentProject]);

    const loadProjects = () => {
        fetch(PROJECTS_URL).then(res => {
            if (res?.status === 'success') {
                const list = res.data || [];
                setProjects(list);
                // Auto-select first active project only if genuinely none selected
                if (!currentProjectRef.current && list.length > 0) {
                    const first = list.find(p => p.is_active) || list[0];
                    switchProject(first);
                }
            }
        });
    };

    useEffect(() => {
        loadProjects();
        window.addEventListener('projects-changed', loadProjects);
        return () => window.removeEventListener('projects-changed', loadProjects);
    }, []);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleSelect = (project) => {
        switchProject(project);
        setOpen(false);
        // Reload page data — easiest way to refresh all project-scoped data
        window.location.reload();
    };

    if (projects.length === 0) return null;

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(o => !o)}
                className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                    "bg-blue-50 text-blue-700 border border-blue-200",
                    "dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700",
                    "hover:bg-blue-100 dark:hover:bg-blue-900/50"
                )}
            >
                <Briefcase className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="max-w-[140px] truncate">
                    {currentProject?.name || 'Select Project'}
                </span>
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
            </button>

            {open && (
                <div className="absolute left-0 top-full mt-1 z-50 w-56 rounded-lg shadow-lg
                                bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 py-1">
                    <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                        Switch Project
                    </p>
                    {projects.filter(p => p.is_active).map(project => (
                        <button
                            key={project.id}
                            onClick={() => handleSelect(project)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left
                                       hover:bg-gray-50 dark:hover:bg-gray-700
                                       text-gray-700 dark:text-gray-200 transition-colors"
                        >
                            <Briefcase className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            <span className="flex-1 truncate">{project.name}</span>
                            {currentProject?.id === project.id && (
                                <Check className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
